import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../utils/api'
import { Plus, Edit2, Trash2, Save, X, AlertCircle, Clock, Layers, Globe, Building2, ChevronDown, Copy, RefreshCw, CalendarDays } from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

type TicketPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'

const PRIORITY_OPTIONS: { value: TicketPriority; label: string; color: string }[] = [
  { value: 'CRITICAL', label: 'Critical', color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'HIGH',     label: 'High',     color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'MEDIUM',   label: 'Medium',   color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { value: 'LOW',      label: 'Low',      color: 'bg-gray-100 text-gray-600 border-gray-200' },
]

const ic = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white'

function fmtTime(mins: number) {
  if (mins < 60)   return `${mins} นาที`
  if (mins < 1440) return `${mins / 60} ชั่วโมง`
  return `${mins / 1440} วัน`
}

function getCurrentUser(): { role: string } | null {
  try { const u = localStorage.getItem('user'); return u ? JSON.parse(u) : null } catch { return null }
}

interface Company { id: string; name: string; code?: string | null }

// ─── Global Badge ─────────────────────────────────────────────────────────────

function GlobalBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-xs bg-purple-50 text-purple-600 border border-purple-200 px-1.5 py-0.5 rounded-full flex-shrink-0">
      <Globe size={9}/> ค่าเริ่มต้น
    </span>
  )
}

// ─── Company Picker (SUPER_ADMIN) ─────────────────────────────────────────────

interface CompanyPickerProps {
  companies: Company[]
  selected: string | null // null = global defaults
  onChange: (id: string | null) => void
}

function CompanyPicker({ companies, selected, onChange }: CompanyPickerProps) {
  const [open, setOpen] = useState(false)
  const active = selected ? companies.find(c => c.id === selected) : null

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm hover:border-gray-300 transition-colors shadow-sm"
      >
        {selected ? <Building2 size={14} className="text-blue-500"/> : <Globe size={14} className="text-purple-500"/>}
        <span className="font-medium text-gray-700">
          {active ? active.name : 'ค่าเริ่มต้น (Global)'}
        </span>
        {active?.code && <span className="text-xs text-gray-400 font-mono">{active.code}</span>}
        <ChevronDown size={13} className="text-gray-400 ml-1"/>
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 min-w-56 overflow-hidden">
          <div className="p-1">
            <button
              onClick={() => { onChange(null); setOpen(false) }}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${selected === null ? 'bg-purple-50 text-purple-700' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <Globe size={13}/> ค่าเริ่มต้น (Global)
            </button>
            {companies.length > 0 && (
              <div className="border-t border-gray-100 mt-1 pt-1">
                <p className="text-xs text-gray-400 px-3 py-1">บริษัท</p>
                {companies.map(c => (
                  <button
                    key={c.id}
                    onClick={() => { onChange(c.id); setOpen(false) }}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${selected === c.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    <Building2 size={13}/>
                    <span className="flex-1 text-left">{c.name}</span>
                    {c.code && <span className="text-xs text-gray-400 font-mono">{c.code}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Seed Defaults Banner ─────────────────────────────────────────────────────

function SeedDefaultsBanner({ companyId, companyName, onDone }: { companyId: string; companyName: string; onDone: () => void }) {
  const [loading, setLoading] = useState(false)

  const seed = async () => {
    setLoading(true)
    try {
      const res = await api.post('/tickets/seed-defaults', { companyId })
      const { categoriesAdded, slasAdded } = res.data
      alert(`คัดลอกสำเร็จ: หมวดหมู่ ${categoriesAdded} รายการ, SLA ${slasAdded} รายการ`)
      onDone()
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setLoading(false) }
  }

  return (
    <div className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-xl px-4 py-3">
      <div className="flex items-center gap-2">
        <Copy size={15} className="text-purple-500"/>
        <p className="text-sm text-purple-800">
          คัดลอกค่าเริ่มต้น (Global) ไปยัง <strong>{companyName}</strong>
        </p>
      </div>
      <button
        onClick={seed}
        disabled={loading}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700 disabled:opacity-50"
      >
        <Copy size={12}/> {loading ? 'กำลังคัดลอก...' : 'คัดลอก'}
      </button>
    </div>
  )
}

// ─── Category Manager ─────────────────────────────────────────────────────────

interface CategoryManagerProps {
  isSuperAdmin: boolean
  targetCompanyId: string | null | undefined
  companies: Company[]
  onSeedDone: () => void
}

function CategoryManager({ isSuperAdmin, targetCompanyId, companies, onSeedDone }: CategoryManagerProps) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  // addingUnder: null = root, string = parentId
  const [addingUnder, setAddingUnder] = useState<string | null | false>(false) // false = not adding
  const [newName, setNewName] = useState('')
  const [loading, setLoading] = useState(false)

  const params: Record<string, string> = isSuperAdmin && targetCompanyId !== undefined
    ? { targetCompanyId: targetCompanyId ?? '' }
    : {}

  const { data: categories = [] } = useQuery<any[]>({
    queryKey: ['ticket-categories', targetCompanyId],
    queryFn: () => api.get('/tickets/categories', { params }).then(r => r.data),
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['ticket-categories'] })

  const startEdit = (cat: any) => { setEditing(cat.id); setEditName(cat.name) }
  const cancelEdit = () => { setEditing(null); setEditName('') }

  const saveEdit = async (id: string) => {
    if (!editName.trim()) return
    setLoading(true)
    try { await api.put(`/tickets/categories/${id}`, { name: editName.trim() }); invalidate(); cancelEdit() }
    catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setLoading(false) }
  }

  const startAdd = (parentId: string | null) => { setAddingUnder(parentId); setNewName(''); setEditing(null) }
  const cancelAdd = () => { setAddingUnder(false); setNewName('') }

  const saveAdd = async () => {
    if (!newName.trim()) return
    setLoading(true)
    try {
      const body: any = { name: newName.trim(), parentId: addingUnder || undefined }
      if (isSuperAdmin && targetCompanyId !== undefined) body.targetCompanyId = targetCompanyId ?? ''
      await api.post('/tickets/categories', body)
      invalidate(); cancelAdd()
    }
    catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setLoading(false) }
  }

  const deleteCategory = async (id: string, name: string) => {
    if (!confirm(`ลบ "${name}"?\nรายการลูกและ Ticket ที่ใช้จะถูกตั้งเป็น "ไม่ระบุ"`)) return
    try { await api.delete(`/tickets/categories/${id}`); invalidate() }
    catch (e: any) { alert(e.response?.data?.message || e.message) }
  }

  const InlineInput = ({ placeholder }: { placeholder: string }) => (
    <div className="flex items-center gap-2 mt-1">
      <input autoFocus value={newName} onChange={e => setNewName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') saveAdd(); if (e.key === 'Escape') cancelAdd() }}
        className="flex-1 px-3 py-1.5 border border-blue-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder={placeholder}/>
      <button onClick={saveAdd} disabled={loading || !newName.trim()}
        className="p-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"><Save size={13}/></button>
      <button onClick={cancelAdd} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400"><X size={13}/></button>
    </div>
  )

  const EditInput = ({ id }: { id: string }) => (
    <div className="flex items-center gap-2 flex-1">
      <input autoFocus value={editName} onChange={e => setEditName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') saveEdit(id); if (e.key === 'Escape') cancelEdit() }}
        className="flex-1 px-3 py-1.5 border border-blue-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
      <button onClick={() => saveEdit(id)} disabled={loading}
        className="p-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"><Save size={13}/></button>
      <button onClick={cancelEdit} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400"><X size={13}/></button>
    </div>
  )

  const canEdit = (cat: any, isReadOnly: boolean) => !isReadOnly && (isSuperAdmin || !cat.isGlobal)

  const renderCatTree = (cats: any[], isReadOnly = false) => cats.map((cat: any) => (
    <div key={cat.id}>
      {/* Category (level 1) */}
      <div className={`flex items-center gap-2 px-5 py-2.5 hover:bg-gray-50 group ${isReadOnly ? 'opacity-70' : ''}`}>
        <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded w-20 text-center flex-shrink-0">Category</span>
        {cat.isGlobal && <Globe size={11} className="text-purple-400 flex-shrink-0"/>}
        {editing === cat.id && canEdit(cat, isReadOnly)
          ? <EditInput id={cat.id}/>
          : <span className="flex-1 text-sm font-medium text-gray-800">{cat.name}</span>
        }
        {canEdit(cat, isReadOnly) && editing !== cat.id && (
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={() => startAdd(cat.id)} title="เพิ่ม Subcategory"
              className="flex items-center gap-1 px-2 py-1 text-xs text-green-600 hover:bg-green-50 rounded-lg"><Plus size={11}/> Sub</button>
            <button onClick={() => startEdit(cat)} className="p-1.5 hover:bg-blue-50 rounded-lg text-gray-400 hover:text-blue-600"><Edit2 size={12}/></button>
            <button onClick={() => deleteCategory(cat.id, cat.name)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-500"><Trash2 size={12}/></button>
          </div>
        )}
        {isReadOnly && <GlobalBadge/>}
      </div>

      {/* Inline add-subcategory input */}
      {addingUnder === cat.id && (
        <div className="px-5 pb-2 pl-14">
          <InlineInput placeholder="ชื่อ Subcategory ใหม่..."/>
        </div>
      )}

      {/* Subcategories (level 2) */}
      {(cat.children || []).map((sub: any) => (
        <div key={sub.id}>
          <div className={`flex items-center gap-2 pl-14 pr-5 py-2 hover:bg-gray-50 group ${isReadOnly ? 'opacity-70' : ''}`}>
            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded w-20 text-center flex-shrink-0">Subcategory</span>
            {sub.isGlobal && <Globe size={11} className="text-purple-400 flex-shrink-0"/>}
            {editing === sub.id && canEdit(sub, isReadOnly)
              ? <EditInput id={sub.id}/>
              : <span className="flex-1 text-sm text-gray-700">{sub.name}</span>
            }
            {canEdit(sub, isReadOnly) && editing !== sub.id && (
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => startAdd(sub.id)} title="เพิ่ม Item"
                  className="flex items-center gap-1 px-2 py-1 text-xs text-indigo-600 hover:bg-indigo-50 rounded-lg"><Plus size={11}/> Item</button>
                <button onClick={() => startEdit(sub)} className="p-1.5 hover:bg-blue-50 rounded-lg text-gray-400 hover:text-blue-600"><Edit2 size={12}/></button>
                <button onClick={() => deleteCategory(sub.id, sub.name)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-500"><Trash2 size={12}/></button>
              </div>
            )}
          </div>

          {/* Inline add-item input */}
          {addingUnder === sub.id && (
            <div className="pl-28 pr-5 pb-2">
              <InlineInput placeholder="ชื่อ Item ใหม่..."/>
            </div>
          )}

          {/* Items (level 3) */}
          {(sub.children || []).map((item: any) => (
            <div key={item.id} className={`flex items-center gap-2 pl-28 pr-5 py-1.5 hover:bg-gray-50 group ${isReadOnly ? 'opacity-70' : ''}`}>
              <span className="text-xs text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded w-20 text-center flex-shrink-0 border border-gray-100">Item</span>
              {item.isGlobal && <Globe size={11} className="text-purple-400 flex-shrink-0"/>}
              {editing === item.id && canEdit(item, isReadOnly)
                ? <EditInput id={item.id}/>
                : <span className="flex-1 text-sm text-gray-600">{item.name}</span>
              }
              {canEdit(item, isReadOnly) && editing !== item.id && (
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => startEdit(item)} className="p-1.5 hover:bg-blue-50 rounded-lg text-gray-400 hover:text-blue-600"><Edit2 size={12}/></button>
                  <button onClick={() => deleteCategory(item.id, item.name)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-500"><Trash2 size={12}/></button>
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  ))

  const ownCats   = categories.filter((c: any) => !c.isGlobal)
  const globalCats = categories.filter((c: any) => c.isGlobal)
  const isCompanyView = isSuperAdmin && typeof targetCompanyId === 'string'
  const showSeedBanner = isCompanyView && ownCats.length === 0 && globalCats.length > 0
  const activeCompany = typeof targetCompanyId === 'string' ? companies.find(c => c.id === targetCompanyId) : null

  const totalOwn = ownCats.reduce((n: number, c: any) => n + 1 + (c.children?.length || 0) + (c.children || []).reduce((m: number, s: any) => m + (s.children?.length || 0), 0), 0)

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
        <div className="flex items-center gap-2">
          <Layers size={16} className="text-gray-400"/>
          <h3 className="font-semibold text-gray-800 text-sm">หมวดหมู่ (Category → Subcategory → Item)</h3>
          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
            {isSuperAdmin && targetCompanyId === null ? categories.length : ownCats.length}
          </span>
          {isSuperAdmin && targetCompanyId === null && (
            <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">ค่าเริ่มต้น</span>
          )}
        </div>
        <button onClick={() => startAdd(null)}
          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700">
          <Plus size={13}/> Category
        </button>
      </div>

      <div className="divide-y divide-gray-50">
        {showSeedBanner && activeCompany && (
          <div className="p-3">
            <SeedDefaultsBanner companyId={activeCompany.id} companyName={activeCompany.name} onDone={() => { invalidate(); onSeedDone() }}/>
          </div>
        )}

        {addingUnder === null && (
          <div className="px-5 py-3 bg-blue-50">
            <p className="text-xs text-blue-600 font-medium mb-1">เพิ่ม Category ใหม่</p>
            <InlineInput placeholder="ชื่อ Category..."/>
          </div>
        )}

        {isSuperAdmin && targetCompanyId === null
          ? renderCatTree(categories)
          : (
            <>
              {renderCatTree(ownCats)}
              {globalCats.length > 0 && (
                <>
                  {ownCats.length > 0 && <div className="px-5 py-2 bg-gray-50"><p className="text-xs text-gray-400 font-medium">ค่าเริ่มต้นระบบ (อ่านอย่างเดียว)</p></div>}
                  {renderCatTree(globalCats, true)}
                </>
              )}
            </>
          )
        }

        {categories.length === 0 && addingUnder === false && (
          <div className="px-5 py-8 text-center text-sm text-gray-400">ยังไม่มีหมวดหมู่</div>
        )}
      </div>
    </div>
  )
}

// ─── SLA Manager ─────────────────────────────────────────────────────────────

interface SLAFormData {
  name: string; priority: TicketPriority; responseMinutes: number; resolveMinutes: number
  businessHoursStart: number; businessHoursEnd: number; businessDays: string; timezone: string
}
const emptySLA: SLAFormData = {
  name: '', priority: 'MEDIUM', responseMinutes: 480, resolveMinutes: 1440,
  businessHoursStart: 9, businessHoursEnd: 18, businessDays: '1,2,3,4,5', timezone: 'Asia/Bangkok',
}

const DAY_OPTIONS = [
  { value: '1', label: 'จ' }, { value: '2', label: 'อ' }, { value: '3', label: 'พ' },
  { value: '4', label: 'พฤ' }, { value: '5', label: 'ศ' }, { value: '6', label: 'ส' }, { value: '7', label: 'อา' },
]

interface SLAManagerProps {
  isSuperAdmin: boolean
  targetCompanyId: string | null | undefined
  companies: Company[]
  onSeedDone: () => void
}

function SLAManager({ isSuperAdmin, targetCompanyId, companies, onSeedDone }: SLAManagerProps) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<any | null>(null)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState<SLAFormData>(emptySLA)
  const [loading, setLoading] = useState(false)

  const params: Record<string, string> = isSuperAdmin && targetCompanyId !== undefined
    ? { targetCompanyId: targetCompanyId ?? '' }
    : {}

  const { data: slas = [] } = useQuery<any[]>({
    queryKey: ['ticket-slas', targetCompanyId],
    queryFn: () => api.get('/tickets/slas', { params }).then(r => r.data),
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['ticket-slas'] })

  const startEdit = (sla: any) => {
    setEditing(sla)
    setForm({
      name: sla.name, priority: sla.priority,
      responseMinutes: sla.responseMinutes, resolveMinutes: sla.resolveMinutes,
      businessHoursStart: sla.businessHoursStart ?? 9, businessHoursEnd: sla.businessHoursEnd ?? 18,
      businessDays: sla.businessDays ?? '1,2,3,4,5', timezone: sla.timezone ?? 'Asia/Bangkok',
    })
    setAdding(false)
  }
  const startAdd = () => { setAdding(true); setEditing(null); setForm(emptySLA) }
  const cancel   = () => { setEditing(null); setAdding(false); setForm(emptySLA) }

  const save = async () => {
    if (!form.name.trim()) return
    setLoading(true)
    try {
      const body: any = { ...form }
      if (isSuperAdmin && targetCompanyId !== undefined) body.targetCompanyId = targetCompanyId ?? ''
      if (editing) {
        await api.put(`/tickets/slas/${editing.id}`, body)
      } else {
        await api.post('/tickets/slas', body)
      }
      invalidate(); cancel()
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setLoading(false) }
  }

  const deleteSLA = async (id: string, name: string) => {
    if (!confirm(`ลบ SLA "${name}"?`)) return
    try { await api.delete(`/tickets/slas/${id}`); invalidate() }
    catch (e: any) { alert(e.response?.data?.message || e.message) }
  }

  const toggleDay = (day: string) => {
    const days = form.businessDays ? form.businessDays.split(',').filter(Boolean) : []
    const next = days.includes(day) ? days.filter(d => d !== day) : [...days, day]
    setForm(f => ({ ...f, businessDays: next.sort().join(',') }))
  }

  const SLAFormPanel = ({ isGlobal }: { isGlobal?: boolean }) => (
    <div className="px-5 py-4 border border-blue-200 rounded-xl bg-blue-50 space-y-3">
      <p className="text-xs font-semibold text-blue-700">{editing ? 'แก้ไข SLA' : 'เพิ่ม SLA ใหม่'}{isGlobal && ' (Global)'}</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 mb-1 block">ชื่อ SLA *</label>
          <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className={ic} placeholder="เช่น High Priority SLA"/>
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1 block">ระดับความสำคัญ</label>
          <select value={form.priority} onChange={e => setForm(f => ({...f, priority: e.target.value as TicketPriority}))} className={ic}>
            {PRIORITY_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 mb-1 block">ต้องตอบสนองภายใน (นาที)</label>
          <input type="number" min={1} value={form.responseMinutes} onChange={e => setForm(f => ({...f, responseMinutes: Number(e.target.value)}))} className={ic}/>
          <p className="text-xs text-gray-400 mt-0.5">= {fmtTime(form.responseMinutes)}</p>
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1 block">ต้องแก้ไขภายใน (นาที)</label>
          <input type="number" min={1} value={form.resolveMinutes} onChange={e => setForm(f => ({...f, resolveMinutes: Number(e.target.value)}))} className={ic}/>
          <p className="text-xs text-gray-400 mt-0.5">= {fmtTime(form.resolveMinutes)}</p>
        </div>
      </div>
      <div className="border-t border-blue-200 pt-3">
        <p className="text-xs font-semibold text-blue-700 mb-2">เวลาทำการ (Business Hours)</p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">เวลาเปิด (ชม.)</label>
            <input type="number" min={0} max={23} value={form.businessHoursStart} onChange={e => setForm(f => ({...f, businessHoursStart: Number(e.target.value)}))} className={ic}/>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">เวลาปิด (ชม.)</label>
            <input type="number" min={1} max={24} value={form.businessHoursEnd} onChange={e => setForm(f => ({...f, businessHoursEnd: Number(e.target.value)}))} className={ic}/>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Timezone</label>
            <input value={form.timezone} onChange={e => setForm(f => ({...f, timezone: e.target.value}))} className={ic} placeholder="Asia/Bangkok"/>
          </div>
        </div>
        <div className="mt-2">
          <label className="text-xs text-gray-500 mb-1 block">วันทำงาน</label>
          <div className="flex gap-1.5 flex-wrap">
            {DAY_OPTIONS.map(d => {
              const active = form.businessDays?.split(',').includes(d.value)
              return (
                <button key={d.value} type="button" onClick={() => toggleDay(d.value)}
                  className={`w-8 h-8 rounded-lg text-xs font-medium border transition-colors ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>
                  {d.label}
                </button>
              )
            })}
          </div>
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={cancel} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs hover:bg-gray-50">ยกเลิก</button>
        <button onClick={save} disabled={loading || !form.name.trim()}
          className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50">
          {loading ? 'กำลังบันทึก...' : 'บันทึก'}
        </button>
      </div>
    </div>
  )

  const sortOrder: TicketPriority[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW']
  const ownSlas   = slas.filter((s: any) => !s.isGlobal)
  const globalSlas = slas.filter((s: any) => s.isGlobal)
  const sorted    = [...ownSlas].sort((a, b) => sortOrder.indexOf(a.priority) - sortOrder.indexOf(b.priority))
  const sortedGlobal = [...globalSlas].sort((a, b) => sortOrder.indexOf(a.priority) - sortOrder.indexOf(b.priority))

  const isCompanyView = isSuperAdmin && typeof targetCompanyId === 'string'
  const showSeedBanner = isCompanyView && ownSlas.length === 0 && globalSlas.length > 0
  const activeCompany = typeof targetCompanyId === 'string' ? companies.find(c => c.id === targetCompanyId) : null

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
        <div className="flex items-center gap-2">
          <Clock size={16} className="text-gray-400"/>
          <h3 className="font-semibold text-gray-800 text-sm">นโยบาย SLA</h3>
          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
            {isSuperAdmin && targetCompanyId === null ? slas.length : ownSlas.length}
          </span>
          {isSuperAdmin && targetCompanyId === null && (
            <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">ค่าเริ่มต้น</span>
          )}
        </div>
        <button onClick={startAdd}
          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700">
          <Plus size={13}/> เพิ่ม
        </button>
      </div>

      <div className="p-4 space-y-3">
        {showSeedBanner && activeCompany && (
          <SeedDefaultsBanner companyId={activeCompany.id} companyName={activeCompany.name} onDone={() => { invalidate(); onSeedDone() }}/>
        )}

        {adding && <SLAFormPanel/>}

        {/* Global-edit mode: SUPER_ADMIN managing global defaults */}
        {isSuperAdmin && targetCompanyId === null && (
          <>
            {[...slas].sort((a, b) => sortOrder.indexOf(a.priority) - sortOrder.indexOf(b.priority)).map((sla: any) => {
              const pc = PRIORITY_OPTIONS.find(p => p.value === sla.priority)
              return editing?.id === sla.id ? (
                <div key={sla.id}><SLAFormPanel isGlobal/></div>
              ) : (
                <div key={sla.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-xl hover:bg-gray-50">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium border flex-shrink-0 ${pc?.color}`}>{pc?.label}</span>
                  <Globe size={12} className="text-purple-400 flex-shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">{sla.name}</p>
                    <p className="text-xs text-gray-400">ตอบสนอง: {fmtTime(sla.responseMinutes)} · แก้ไข: {fmtTime(sla.resolveMinutes)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => startEdit(sla)} className="p-1.5 hover:bg-blue-50 rounded-lg text-gray-400 hover:text-blue-600"><Edit2 size={13}/></button>
                    <button onClick={() => deleteSLA(sla.id, sla.name)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-500"><Trash2 size={13}/></button>
                  </div>
                </div>
              )
            })}
            {slas.length === 0 && !adding && (
              <div className="text-center py-6 text-sm text-gray-400">ยังไม่มี SLA Policy เริ่มต้น</div>
            )}
          </>
        )}

        {/* Company view mode: own SLAs + global defaults (read-only) */}
        {!(isSuperAdmin && targetCompanyId === null) && (
          <>
            {sorted.map((sla: any) => {
              const pc = PRIORITY_OPTIONS.find(p => p.value === sla.priority)
              return editing?.id === sla.id ? (
                <div key={sla.id}><SLAFormPanel/></div>
              ) : (
                <div key={sla.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-xl hover:bg-gray-50">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium border flex-shrink-0 ${pc?.color}`}>{pc?.label}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">{sla.name}</p>
                    <p className="text-xs text-gray-400">ตอบสนอง: {fmtTime(sla.responseMinutes)} · แก้ไข: {fmtTime(sla.resolveMinutes)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => startEdit(sla)} className="p-1.5 hover:bg-blue-50 rounded-lg text-gray-400 hover:text-blue-600"><Edit2 size={13}/></button>
                    <button onClick={() => deleteSLA(sla.id, sla.name)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-500"><Trash2 size={13}/></button>
                  </div>
                </div>
              )
            })}

            {sortedGlobal.length > 0 && (
              <>
                {ownSlas.length > 0 && (
                  <p className="text-xs text-gray-400 font-medium pt-1">ค่าเริ่มต้นระบบ (อ่านอย่างเดียว)</p>
                )}
                {sortedGlobal.map((sla: any) => {
                  const pc = PRIORITY_OPTIONS.find(p => p.value === sla.priority)
                  return (
                    <div key={sla.id} className="flex items-center gap-3 p-3 border border-purple-100 rounded-xl bg-purple-50/30 opacity-70">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium border flex-shrink-0 ${pc?.color}`}>{pc?.label}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700">{sla.name}</p>
                        <p className="text-xs text-gray-400">ตอบสนอง: {fmtTime(sla.responseMinutes)} · แก้ไข: {fmtTime(sla.resolveMinutes)}</p>
                      </div>
                      <GlobalBadge/>
                    </div>
                  )
                })}
              </>
            )}

            {slas.length === 0 && !adding && (
              <div className="text-center py-6 text-sm text-gray-400">ยังไม่มี SLA Policy</div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ─── Ticket Types (static display) ───────────────────────────────────────────

function TicketTypesInfo() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-50">
        <AlertCircle size={16} className="text-gray-400"/>
        <h3 className="font-semibold text-gray-800 text-sm">ประเภท Ticket</h3>
      </div>
      <div className="p-4 space-y-2">
        {[
          { label: 'Incident',        desc: 'ปัญหาหรือความผิดปกติที่เกิดขึ้น เช่น ระบบล่ม, อุปกรณ์เสีย',         color: 'bg-red-50 text-red-700 border-red-200' },
          { label: 'Service Request', desc: 'คำขอบริการ เช่น ขอติดตั้งซอฟต์แวร์, ขอสิทธิ์การเข้าถึง',            color: 'bg-blue-50 text-blue-700 border-blue-200' },
          { label: 'Change',          desc: 'คำขอเปลี่ยนแปลงระบบ เช่น อัปเกรดซอฟต์แวร์, เปลี่ยนค่าคอนฟิก',      color: 'bg-teal-50 text-teal-700 border-teal-200' },
        ].map(t => (
          <div key={t.label} className="flex items-start gap-3 p-3 border border-gray-100 rounded-xl">
            <span className={`text-xs px-2 py-0.5 rounded font-medium border flex-shrink-0 mt-0.5 ${t.color}`}>{t.label}</span>
            <p className="text-sm text-gray-600">{t.desc}</p>
          </div>
        ))}
        <p className="text-xs text-gray-400 text-center pt-1">ประเภท Ticket เป็นค่าคงที่ของระบบ ไม่สามารถเพิ่มหรือลบได้</p>
      </div>
    </div>
  )
}

// ─── Business Hours & Holidays Manager ───────────────────────────────────────

interface Holiday { date: string; name: string }
interface BizHoursForm {
  businessHoursStart: number
  businessHoursEnd: number
  businessDays: string
  timezone: string
  holidays: Holiday[]
}

const BIZ_DAY_OPTIONS = [
  { value: '1', label: 'จ' }, { value: '2', label: 'อ' }, { value: '3', label: 'พ' },
  { value: '4', label: 'พฤ' }, { value: '5', label: 'ศ' }, { value: '6', label: 'ส' }, { value: '7', label: 'อา' },
]

const COMMON_TIMEZONES = [
  'Asia/Bangkok', 'Asia/Singapore', 'Asia/Tokyo', 'Asia/Shanghai',
  'Asia/Kolkata', 'Europe/London', 'Europe/Paris', 'America/New_York', 'America/Los_Angeles', 'UTC',
]

const DEFAULT_BIZ: BizHoursForm = {
  businessHoursStart: 8, businessHoursEnd: 17,
  businessDays: '1,2,3,4,5', timezone: 'Asia/Bangkok', holidays: [],
}

interface BusinessHoursManagerProps {
  isSuperAdmin: boolean
  targetCompanyId: string | null | undefined
}

function BusinessHoursManager({ isSuperAdmin, targetCompanyId }: BusinessHoursManagerProps) {
  const userCompanyId = useMemo(() => {
    try { const u = localStorage.getItem('user'); return u ? JSON.parse(u).companyId : null }
    catch { return null }
  }, [])

  const companyId = isSuperAdmin ? targetCompanyId : userCompanyId
  const qc = useQueryClient()

  const [form, setForm] = useState<BizHoursForm>(DEFAULT_BIZ)
  const [newHolidayDate, setNewHolidayDate] = useState('')
  const [newHolidayName, setNewHolidayName] = useState('')
  const [saved, setSaved] = useState(false)

  const { data, isLoading } = useQuery<BizHoursForm>({
    queryKey: ['sd-biz-hours', companyId],
    queryFn: () => api.get(`/settings/companies/${companyId}/servicedesk`).then(r => r.data),
    enabled: !!companyId,
  })

  useEffect(() => {
    if (data) setForm({ ...DEFAULT_BIZ, ...data })
  }, [data])

  const saveMut = useMutation({
    mutationFn: (body: BizHoursForm) => api.put(`/settings/companies/${companyId}/servicedesk`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sd-biz-hours'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    },
    onError: (e: any) => alert(e.response?.data?.message || e.message),
  })

  const toggleDay = (day: string) => {
    const days = form.businessDays ? form.businessDays.split(',').filter(Boolean) : []
    const next = days.includes(day) ? days.filter(d => d !== day) : [...days, day]
    setForm(f => ({ ...f, businessDays: next.sort().join(',') }))
  }

  const addHoliday = () => {
    if (!newHolidayDate) return
    const holiday: Holiday = { date: newHolidayDate, name: newHolidayName.trim() || newHolidayDate }
    setForm(f => ({ ...f, holidays: [...f.holidays.filter(h => h.date !== newHolidayDate), holiday].sort((a, b) => a.date.localeCompare(b.date)) }))
    setNewHolidayDate('')
    setNewHolidayName('')
  }

  const removeHoliday = (date: string) =>
    setForm(f => ({ ...f, holidays: f.holidays.filter(h => h.date !== date) }))

  if (!companyId) {
    if (isSuperAdmin) return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-2">
          <CalendarDays size={16} className="text-gray-400"/>
          <h3 className="font-semibold text-gray-800 text-sm">เวลาทำการ & วันหยุด</h3>
        </div>
        <p className="text-sm text-gray-400 text-center py-4">กรุณาเลือก Company เพื่อตั้งค่า Business Hours</p>
      </div>
    )
    return null
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
        <div className="flex items-center gap-2">
          <CalendarDays size={16} className="text-gray-400"/>
          <h3 className="font-semibold text-gray-800 text-sm">เวลาทำการ & วันหยุด</h3>
          <span className="text-xs text-gray-400">(ใช้ในการคำนวณ SLA)</span>
        </div>
        {isLoading && <RefreshCw size={14} className="text-gray-400 animate-spin"/>}
      </div>

      <div className="p-5 space-y-5">
        {/* Business Hours */}
        <div>
          <p className="text-xs font-semibold text-gray-600 mb-3">ชั่วโมงทำการ</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">เวลาเปิด (ชม. 0–23)</label>
              <input type="number" min={0} max={23} value={form.businessHoursStart}
                onChange={e => setForm(f => ({ ...f, businessHoursStart: Number(e.target.value) }))}
                className={ic}/>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">เวลาปิด (ชม. 1–24)</label>
              <input type="number" min={1} max={24} value={form.businessHoursEnd}
                onChange={e => setForm(f => ({ ...f, businessHoursEnd: Number(e.target.value) }))}
                className={ic}/>
            </div>
          </div>
        </div>

        {/* Working Days */}
        <div>
          <label className="text-xs text-gray-500 mb-2 block">วันทำงาน</label>
          <div className="flex gap-1.5 flex-wrap">
            {BIZ_DAY_OPTIONS.map(d => {
              const active = form.businessDays?.split(',').includes(d.value)
              return (
                <button key={d.value} type="button" onClick={() => toggleDay(d.value)}
                  className={`w-9 h-9 rounded-lg text-xs font-medium border transition-colors ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>
                  {d.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Timezone */}
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Timezone</label>
          <select value={form.timezone} onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))} className={ic}>
            {COMMON_TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </div>

        {/* Holidays */}
        <div>
          <p className="text-xs font-semibold text-gray-600 mb-2">วันหยุดนักขัตฤกษ์</p>
          <div className="space-y-1.5 mb-3">
            {form.holidays.length === 0 && (
              <p className="text-xs text-gray-400 py-2 text-center">ยังไม่มีวันหยุดที่กำหนด</p>
            )}
            {form.holidays.map(h => (
              <div key={h.date} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                <span className="text-xs font-mono text-blue-600 flex-shrink-0">{h.date}</span>
                <span className="text-xs text-gray-700 flex-1">{h.name}</span>
                <button onClick={() => removeHoliday(h.date)} className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500">
                  <X size={12}/>
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input type="date" value={newHolidayDate} onChange={e => setNewHolidayDate(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            <input type="text" value={newHolidayName} onChange={e => setNewHolidayName(e.target.value)}
              placeholder="ชื่อวันหยุด..." onKeyDown={e => e.key === 'Enter' && addHoliday()}
              className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            <button onClick={addHoliday} disabled={!newHolidayDate}
              className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-40">
              <Plus size={12}/> เพิ่ม
            </button>
          </div>
        </div>

        <div className="flex justify-end pt-1">
          <button
            onClick={() => saveMut.mutate(form)}
            disabled={saveMut.isPending}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${saved ? 'bg-green-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50'}`}>
            <Save size={13}/>
            {saveMut.isPending ? 'กำลังบันทึก...' : saved ? 'บันทึกแล้ว ✓' : 'บันทึก'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Assignment Rule Manager ──────────────────────────────────────────────────

function AssignmentRuleManager() {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({ categoryId: '', assigneeId: '', taskTitle: 'ตรวจสอบเบื้องต้น' })
  const [loading, setLoading] = useState(false)

  const { data: rules = [] } = useQuery<any[]>({
    queryKey: ['assignment-rules'],
    queryFn: () => api.get('/tickets/assignment-rules').then(r => r.data),
  })
  const { data: categoryTree = [] } = useQuery<any[]>({
    queryKey: ['ticket-categories'],
    queryFn: () => api.get('/tickets/categories').then(r => r.data),
  })
  const { data: users = [] } = useQuery<any[]>({
    queryKey: ['users-list'],
    queryFn: () => api.get('/users').then(r => r.data?.users || r.data || []),
  })

  const flatCategories: any[] = []
  for (const root of categoryTree as any[]) {
    flatCategories.push({ ...root, depth: 0 })
    for (const sub of root.children || []) {
      flatCategories.push({ ...sub, depth: 1, _parentName: root.name })
      for (const item of sub.children || []) {
        flatCategories.push({ ...item, depth: 2, _parentName: `${root.name} › ${sub.name}` })
      }
    }
  }

  const save = async () => {
    if (!form.categoryId || !form.assigneeId || !form.taskTitle.trim()) return
    setLoading(true)
    try {
      await api.post('/tickets/assignment-rules', form)
      qc.invalidateQueries({ queryKey: ['assignment-rules'] })
      setAdding(false); setForm({ categoryId: '', assigneeId: '', taskTitle: 'ตรวจสอบเบื้องต้น' })
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setLoading(false) }
  }

  const deleteRule = async (id: string) => {
    if (!confirm('ลบกฎนี้?')) return
    try { await api.delete(`/tickets/assignment-rules/${id}`); qc.invalidateQueries({ queryKey: ['assignment-rules'] }) }
    catch (e: any) { alert(e.response?.data?.message || e.message) }
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
        <div className="flex items-center gap-2">
          <Layers size={16} className="text-gray-400"/>
          <h3 className="font-semibold text-gray-800 text-sm">สร้าง Task อัตโนมัติ (Auto-Task Rules)</h3>
          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{(rules as any[]).length}</span>
        </div>
        <button onClick={() => setAdding(a => !a)}
          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700">
          <Plus size={13}/> เพิ่มกฎ
        </button>
      </div>
      <div className="p-4 space-y-3">
        <p className="text-xs text-gray-500">เมื่อสร้าง Ticket ที่ตรงกับหมวดหมู่นี้ ระบบจะสร้าง Task ตามที่กำหนดและมอบหมายให้ผู้รับผิดชอบ พร้อมส่งอีเมลแจ้งเตือน — ไม่กระทบผู้รับผิดชอบ (Technician) ของตัว Ticket</p>
        {adding && (
          <div className="p-4 border border-blue-200 rounded-xl bg-blue-50 space-y-3">
            <p className="text-xs font-semibold text-blue-700">เพิ่มกฎสร้าง Task อัตโนมัติ</p>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">หมวดหมู่ *</label>
              <select value={form.categoryId} onChange={e => setForm(f => ({...f, categoryId: e.target.value}))} className={ic}>
                <option value="">— เลือกหมวดหมู่ —</option>
                <option value="ALL">— ทุกหมวดหมู่ (All) —</option>
                {flatCategories.map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {'  '.repeat(c.depth)}{c.depth > 0 ? '› ' : ''}{c.name}
                    {c._parentName ? ` (${c._parentName})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">ชื่อ Task ที่จะสร้าง *</label>
              <input value={form.taskTitle} onChange={e => setForm(f => ({...f, taskTitle: e.target.value}))} className={ic} placeholder="เช่น ตรวจสอบเบื้องต้น"/>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">ผู้รับผิดชอบ Task *</label>
              <select value={form.assigneeId} onChange={e => setForm(f => ({...f, assigneeId: e.target.value}))} className={ic}>
                <option value="">— เลือกผู้รับผิดชอบ —</option>
                {(users as any[]).filter((u: any) => ['ADMIN','TECHNICIAN'].includes(u.role)).map((u: any) => (
                  <option key={u.id} value={u.id}>{u.displayName || u.username} ({u.role})</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setAdding(false)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs hover:bg-gray-50">ยกเลิก</button>
              <button onClick={save} disabled={loading || !form.categoryId || !form.assigneeId || !form.taskTitle.trim()}
                className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50">
                {loading ? 'กำลังบันทึก...' : 'บันทึก'}
              </button>
            </div>
          </div>
        )}
        {(rules as any[]).length === 0 && !adding ? (
          <p className="text-sm text-gray-400 text-center py-4">ยังไม่มีกฎสร้าง Task อัตโนมัติ</p>
        ) : (rules as any[]).map((rule: any) => (
          <div key={rule.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-xl hover:bg-gray-50">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800">
                {rule.categoryPath || rule.category?.name}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">สร้าง Task: "{rule.taskTitle}" → {rule.assignee?.displayName || rule.assignee?.username}</p>
            </div>
            <button onClick={() => deleteRule(rule.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-500"><Trash2 size={13}/></button>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function ServiceDeskSettingsPage() {
  const currentUser = getCurrentUser()
  const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN'
  const qc = useQueryClient()

  // SUPER_ADMIN: null = global defaults, string = specific company
  const [targetCompanyId, setTargetCompanyId] = useState<string | null>(null)

  const { data: companies = [] } = useQuery<Company[]>({
    queryKey: ['masterdata', 'companies'],
    queryFn: () => api.get('/masterdata/companies').then(r => r.data),
    enabled: isSuperAdmin,
  })

  const handleSeedDone = () => {
    qc.invalidateQueries({ queryKey: ['ticket-categories'] })
    qc.invalidateQueries({ queryKey: ['ticket-slas'] })
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-1">ServiceDesk Settings</h2>
          <p className="text-xs text-gray-400">จัดการหมวดหมู่, SLA และประเภทของ Ticket</p>
        </div>
        {isSuperAdmin && (
          <div className="flex flex-col items-end gap-1">
            <CompanyPicker
              companies={companies}
              selected={targetCompanyId}
              onChange={setTargetCompanyId}
            />
            {targetCompanyId === null && (
              <p className="text-xs text-purple-500">ตั้งค่าเริ่มต้นที่ใช้กับทุกบริษัท</p>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <CategoryManager
          isSuperAdmin={isSuperAdmin}
          targetCompanyId={isSuperAdmin ? targetCompanyId : undefined}
          companies={companies}
          onSeedDone={handleSeedDone}
        />
        <TicketTypesInfo/>
      </div>
      <BusinessHoursManager
        isSuperAdmin={isSuperAdmin}
        targetCompanyId={isSuperAdmin ? targetCompanyId : undefined}
      />
      <SLAManager
        isSuperAdmin={isSuperAdmin}
        targetCompanyId={isSuperAdmin ? targetCompanyId : undefined}
        companies={companies}
        onSeedDone={handleSeedDone}
      />
      <AssignmentRuleManager/>
    </div>
  )
}
